import { useState, useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MenuItem } from "../types/menuItem";
import { usePagination } from "./usePagination";
import { useAuthenticatedApi } from "./useAuthenticatedApi";
import {
  getMenuSchedules,
  deleteMenuSchedule,
  MenuSchedule,
} from "../api/menuItemsApi";
import { useScheduleIntervals } from "./useScheduleIntervals";

interface Restaurant {
  restaurantId: string;
  hotelId: string;
  name: string;
  description: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Menu {
  restaurantMenuId: string;
  restaurantId: string;
  menuName: string;
  isRoomService: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ApiMenuResponse {
  restaurantmenuid: string;
  restaurantid: string;
  menuname: string;
  isroomservice: boolean;
  createdat: string;
  updatedat: string;
}

interface ApiRestaurant {
  restaurantid?: string;
  restaurantId?: string;
  id?: string;
  hotelid?: string;
  hotelId?: string;
  name: string;
  description: string;
  isactive?: boolean;
  isActive?: boolean;
  createdat?: string;
  createdAt?: string;
  updatedat?: string;
  updatedAt?: string;
}

interface ApiMenuItem {
  price?: {
    priceId?: string;
    amount?: string;
    currencyCode?: string;
  };
  priceAmount?: number;
  currencyCode?: string;
  [key: string]: unknown;
}

interface UseMenuItemsOptions {
  hotelId: string;
  restaurantId: string;
  initialPage?: number;
  initialLimit?: number;
}

export const useMenuItems = ({
  hotelId,
  restaurantId,
  initialPage = 1,
  initialLimit = 20,
}: UseMenuItemsOptions) => {
  const { callApi } = useAuthenticatedApi();
  const queryClient = useQueryClient();

  const [items, setItems] = useState<MenuItem[]>([]);
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [menus, setMenus] = useState<Menu[]>([]);
  const [selectedMenuId, setSelectedMenuId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [cache, setCache] = useState<Record<string, MenuItem>>({});
  const [isRestaurantsLoaded, setIsRestaurantsLoaded] = useState(false);
  const [isMenusLoaded, setIsMenusLoaded] = useState(false);

  // Menu schedule state
  const [menuSchedules, setMenuSchedules] = useState<MenuSchedule[]>([]);
  const [isLoadingMenuSchedules, setIsLoadingMenuSchedules] = useState(false);
  const [menuSchedulesError, setMenuSchedulesError] = useState<Error | null>(
    null
  );

  // Schedule intervals for finding/creating intervals
  const {
    findOrCreateIntervalId,
  } = useScheduleIntervals({ hotelId });

  const { pagination, setPage, setLimit, setTotal } = usePagination({
    initialPage,
    initialLimit,
  });

  // START_MODIFICATION: reset menu-related state when restaurant changes
  useEffect(() => {
    // Clear menu selection and related data whenever the restaurant context changes
    setSelectedMenuId("");
    setMenus([]);
    setIsMenusLoaded(false);
    setItems([]);
    setTotal(0);
  }, [restaurantId]);
  // END_MODIFICATION

  // Cache management
  const updateCache = useCallback((item: MenuItem) => {
    setCache((prev) => ({
      ...prev,
      [item.menuitemid]: item,
    }));
  }, []);

  const removeFromCache = useCallback((id: string) => {
    setCache((prev) => {
      const newCache = { ...prev };
      delete newCache[id];
      return newCache;
    });
  }, []);

  // Helper to normalize API menu item into MenuItem shape with priceAmount/currencyCode populated
  const transformApiItem = (apiItem: ApiMenuItem): MenuItem => {
    const priceObj = (apiItem as ApiMenuItem).price || {};
    const priceAmountNum = priceObj.amount
      ? parseFloat(priceObj.amount as string)
      : (apiItem as Partial<ApiMenuItem>).priceAmount || 0;

    return {
      ...apiItem,
      price:
        priceObj.priceId || (apiItem as Partial<{ price: string }>).price || "",
      priceAmount: isNaN(priceAmountNum) ? 0 : priceAmountNum,
      currencyCode:
        priceObj.currencyCode ||
        (apiItem as Partial<ApiMenuItem>).currencyCode ||
        "USD",
    } as MenuItem;
  };

  // Fetch restaurants
  const fetchRestaurants = useCallback(async () => {
    if (!hotelId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await callApi({
        method: "GET",
        url: `/management/hotel/restaurants`,
      });

      const rawRestaurants: ApiRestaurant[] = Array.isArray(response.data?.data) 
        ? response.data.data 
        : Array.isArray(response.data) 
        ? response.data 
        : [];
      const transformed: Restaurant[] = rawRestaurants.map((r) => ({
        restaurantId: r.restaurantid || r.restaurantId || r.id!,
        hotelId: r.hotelid || r.hotelId!,
        name: r.name,
        description: r.description,
        isActive: r.isactive !== undefined ? r.isactive : r.isActive ?? true,
        createdAt: r.createdat || r.createdAt || "",
        updatedAt: r.updatedat || r.updatedAt || "",
      }));

      setRestaurants(transformed);
      setIsRestaurantsLoaded(true);
    } catch (error) {
      console.error("Error fetching restaurants:", error);
      setError(
        error instanceof Error
          ? error
          : new Error("Failed to fetch restaurants")
      );
      setRestaurants([]);
      setIsRestaurantsLoaded(false);
    } finally {
      setIsLoading(false);
    }
  }, [hotelId, callApi]);

  // Fetch menus
  const fetchMenus = useCallback(async () => {
    if (!hotelId || !restaurantId || !isRestaurantsLoaded) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await callApi({
        method: "GET",
        url: `/management/hotel/restaurants/${restaurantId}/menus`,
      });

      const transformedMenus = (response.data.data || []).map(
        (menu: ApiMenuResponse) => ({
          restaurantMenuId: menu.restaurantmenuid,
          restaurantId: menu.restaurantid,
          menuName: menu.menuname,
          isRoomService: menu.isroomservice,
          createdAt: menu.createdat,
          updatedAt: menu.updatedat,
        })
      );

      setMenus(transformedMenus);
      setIsMenusLoaded(true);
    } catch (error) {
      console.error("Error fetching menus:", error);
      setError(
        error instanceof Error ? error : new Error("Failed to fetch menus")
      );
      setMenus([]);
      setIsMenusLoaded(false);
    } finally {
      setIsLoading(false);
    }
  }, [hotelId, restaurantId, callApi, isRestaurantsLoaded]);

  const {
    data: menuItemsResponse,
    isLoading: queryLoading,
    error: queryError,
    refetch: refetchMenuItems,
  } = useQuery({
    queryKey: [
      "menu-items",
      hotelId,
      restaurantId,
      selectedMenuId,
      pagination.page,
      pagination.limit,
    ],
    enabled: !!restaurantId && !!selectedMenuId && isMenusLoaded,
    queryFn: async () => {
      const response = await callApi({
        method: "GET",
        url: `/management/hotel/restaurants/${restaurantId}/menus/${selectedMenuId}/items`,
        params: {
          page: pagination.page,
          limit: pagination.limit,
        },
      });

      const apiItems: unknown[] = response.data.data || ([] as unknown[]);
      const transformedItems: MenuItem[] = apiItems.map((rawItem) =>
        transformApiItem(rawItem as ApiMenuItem)
      );

      const total =
        typeof response.data.meta?.totalCount === "number"
          ? response.data.meta.totalCount
          : transformedItems.length;

      return {
        items: transformedItems,
        total,
      };
    },
  });

  useEffect(() => {
    setIsLoading(queryLoading);
  }, [queryLoading]);

  useEffect(() => {
    if (!queryError) return;

    setError(
      queryError instanceof Error
        ? queryError
        : new Error("Failed to fetch items")
    );
  }, [queryError]);

  useEffect(() => {
    if (!menuItemsResponse) {
      return;
    }

    const { items: newItems, total } = menuItemsResponse;
    setItems(newItems);
    setTotal(total);
    newItems.forEach((item) => updateCache(item));
  }, [menuItemsResponse, setTotal, updateCache]);

  // Initial fetch
  useEffect(() => {
    fetchRestaurants();
  }, [fetchRestaurants]);

  // Only fetch menus when restaurant changes and restaurants are loaded
  useEffect(() => {
    if (restaurantId && isRestaurantsLoaded) {
      fetchMenus();
    }
  }, [restaurantId, fetchMenus, isRestaurantsLoaded]);

  const fetchItems = useCallback(async () => {
    await refetchMenuItems();
  }, [refetchMenuItems]);

  // Create item with price creation logic
  const createItem = useCallback(
    async (
      userInputItem: Omit<
        MenuItem,
        | "menuitemid"
        | "hotelid"
        | "restaurantmenuid"
        | "createdat"
        | "updatedat"
      >
    ) => {
      if (!selectedMenuId) throw new Error("No menu selected");

      try {
        // First, create a price for the item since price ID is required
        const priceAmount = userInputItem.priceAmount || 0;

        const priceResponse = await callApi({
          method: "POST",
          url: `/management/prices`,
          data: {
            amount: priceAmount.toString(),
            currencyCode: userInputItem.currencyCode || "USD",
            priceType: "Menu Item",
            description: `Price for ${userInputItem.itemname}`,
          },
        });

        const createdPriceId = priceResponse.data?.priceId;
        if (!createdPriceId) {
          throw new Error("Failed to create price for menu item");
        }

        // Now create the menu item with the price ID
        const itemToCreateForApi = {
          itemname: userInputItem.itemname,
          description: userInputItem.description || null,
          ingredients: userInputItem.ingredients || null,
          category: userInputItem.category || null,
          photo: userInputItem.photo || null,
          price: createdPriceId, // Use the created price ID
        };

        const response = await callApi({
          method: "POST",
          url: `/management/hotel/restaurants/${restaurantId}/menus/${selectedMenuId}/items`,
          data: itemToCreateForApi,
        });

        const createdItemFromApi = response.data.data;

        // Construct the item for immediate UI update
        const itemForUiUpdate: MenuItem = {
          ...createdItemFromApi,
          price: createdPriceId,
          priceAmount: userInputItem.priceAmount,
          currencyCode: userInputItem.currencyCode || "USD",
        };

        // Update list and cache
        setItems((prev) => [...prev, itemForUiUpdate]);
        updateCache(itemForUiUpdate);

        // Retry-fetch the created item until backend attaches price properly (avoid full page reload)
        try {
          const createdId = (createdItemFromApi.menuitemid ||
            createdItemFromApi.menuItemId) as string;
          for (let attempt = 0; attempt < 3; attempt++) {
            // Small delay between attempts
            await new Promise((r) => setTimeout(r, 300));
            const retryResp = await callApi({
              method: "GET",
              url: `/management/hotel/restaurants/${restaurantId}/menus/${selectedMenuId}/items`,
              params: { page: 1, limit: pagination.limit },
            });
            const rawItems: ApiMenuItem[] = (retryResp.data?.data ||
              []) as ApiMenuItem[];
            const maybeFresh = rawItems.find(
              (ri: any) =>
                (ri as any).menuitemid === createdId ||
                (ri as any).menuItemId === createdId
            );
            if (maybeFresh) {
              const transformed = transformApiItem(maybeFresh);
              // Consider it fresh if priceAmount parsed > 0 OR price object exists with amount
              const hasPrice = (maybeFresh as any).price?.amount !== undefined;
              if (hasPrice || (transformed.priceAmount ?? 0) > 0) {
                setItems((prev) =>
                  prev.map((it) =>
                    it.menuitemid === createdId ? transformed : it
                  )
                );
                updateCache(transformed);
                break;
              }
            }
          }
        } catch {
          // non-fatal; keep optimistic values
        }

        await queryClient.invalidateQueries({
          queryKey: ["menu-items", hotelId, restaurantId, selectedMenuId],
        });

        return itemForUiUpdate;
      } catch (error) {
        console.error("Error creating menu item:", error);
        throw error;
      }
    },
    [hotelId, restaurantId, selectedMenuId, callApi, updateCache]
  );

  // Update item with optimistic update
  const updateItem = useCallback(
    async (id: string, updates: Partial<MenuItem>) => {
      if (!selectedMenuId) throw new Error("No menu selected");

      // Find item by ID
      const originalItem = cache[id];
      if (!originalItem) throw new Error("Item not found");

      // ----- Optimistic update so UI changes immediately -----
      const optimisticallyUpdated: MenuItem = transformApiItem({
        ...(originalItem as unknown as ApiMenuItem),
        ...(updates as unknown as ApiMenuItem),
      });

      setItems((prev) =>
        prev.map((item) =>
          item.menuitemid === id ? optimisticallyUpdated : item
        )
      );
      updateCache(optimisticallyUpdated);

      const updatesToSend = {
        ...updates,
        hotelid: hotelId,
        restaurantmenuid: selectedMenuId,
      } as Record<string, unknown>;

      // If priceAmount or currencyCode is changing, create/find a priceId
      if (
        updates.priceAmount !== undefined ||
        updates.currencyCode !== undefined
      ) {
        if (originalItem.price) {
          // Update existing price record
          await callApi({
            method: "PUT",
            url: `/management/prices/${originalItem.price}`,
            data: {
              amount: (
                updates.priceAmount ?? originalItem.priceAmount
              ).toString(),
              currencyCode: updates.currencyCode ?? originalItem.currencyCode,
            },
          });
        }
        // Remove priceAmount & currencyCode from item update payload to avoid backend validation errors
        delete (updatesToSend as Record<string, unknown>).priceAmount;
        delete (updatesToSend as Record<string, unknown>).currencyCode;
      }

      try {
        const response = await callApi({
          method: "PUT",
          url: `/management/hotel/restaurants/${restaurantId}/menus/${selectedMenuId}/items/${id}`,
          data: updatesToSend,
        });

        const apiResult = response.data.data as ApiMenuItem;
        const merged = {
          ...(originalItem as unknown as ApiMenuItem),
          ...(updates as unknown as ApiMenuItem),
          ...apiResult,
        } as ApiMenuItem;
        const transformed = transformApiItem(merged);

        // Update UI state and cache with authoritative server version
        setItems((prev) =>
          prev.map((item) => (item.menuitemid === id ? transformed : item))
        );
        updateCache(transformed);

        await queryClient.invalidateQueries({
          queryKey: ["menu-items", hotelId, restaurantId, selectedMenuId],
        });

        return transformed;
      } catch (error) {
        // If the update fails, revert to the original item
        setItems((prev) =>
          prev.map((item) => (item.menuitemid === id ? originalItem : item))
        );
        updateCache(originalItem);
        throw error;
      }
    },
    [hotelId, restaurantId, selectedMenuId, cache, callApi, updateCache]
  );

  // Update item price with optimistic update
  const updateItemPrice = useCallback(
    async (id: string, amount: number, currencyCode: string) => {
      if (!selectedMenuId) throw new Error("No menu selected");

      // Find item by ID
      const originalItem = cache[id];
      if (!originalItem) throw new Error("Item not found");

      if (!originalItem.price) {
        console.warn(
          "Menu item is missing priceId; cannot update price directly"
        );
        return originalItem;
      }

      try {
        // First update the price record itself
        await callApi({
          method: "PUT",
          url: `/management/prices/${originalItem.price}`,
          data: {
            amount: amount.toString(),
            currencyCode,
          },
        });

        // Locally update the item
        const updatedItem: MenuItem = {
          ...originalItem,
          priceAmount: amount,
          currencyCode,
        };

        setItems((prev) =>
          prev.map((item) => (item.menuitemid === id ? updatedItem : item))
        );
        updateCache(updatedItem);

        await queryClient.invalidateQueries({
          queryKey: ["menu-items", hotelId, restaurantId, selectedMenuId],
        });

        return updatedItem;
      } catch (error) {
        console.error("Error updating menu item price:", error);
        throw error;
      }
    },
    [hotelId, restaurantId, selectedMenuId, cache, callApi, updateCache]
  );

  // Delete item
  const deleteItem = useCallback(
    async (id: string) => {
      if (!selectedMenuId) throw new Error("No menu selected");

      try {
        await callApi({
          method: "DELETE",
          url: `/management/hotel/restaurants/${restaurantId}/menus/${selectedMenuId}/items/${id}`,
        });

        // Update the items list and cache
        setItems((prev) => prev.filter((item) => item.menuitemid !== id));
        removeFromCache(id);

        await queryClient.invalidateQueries({
          queryKey: ["menu-items", hotelId, restaurantId, selectedMenuId],
        });
      } catch (error) {
        console.error("Error deleting menu item:", error);
        throw error;
      }
    },
    [hotelId, restaurantId, selectedMenuId, callApi, removeFromCache]
  );

  // Menu schedule functions
  const fetchMenuSchedules = useCallback(
    async (restaurantId: string, menuId: string) => {
      if (!restaurantId || !menuId) {
        setMenuSchedules([]);
        return;
      }

      setIsLoadingMenuSchedules(true);
      setMenuSchedulesError(null);
      try {
        const schedules = await getMenuSchedules(callApi, restaurantId, menuId);
        setMenuSchedules(schedules);
      } catch (err: unknown) {
        console.error("Error fetching menu schedules:", err);

        // Check if it's an authentication error
        if (
          err instanceof Error &&
          err.message.includes("Authentication required")
        ) {
          // Don't set error state for auth redirects, just clear data
          setMenuSchedules([]);
          return;
        }

        setMenuSchedulesError(
          err instanceof Error
            ? err
            : new Error("Failed to fetch menu schedules")
        );
      } finally {
        setIsLoadingMenuSchedules(false);
      }
    },
    [callApi]
  );

  const createMenuScheduleForDay = useCallback(
    async (day: string, startTime: string, endTime: string) => {
      if (!restaurantId || !selectedMenuId || !hotelId) return;

      try {
        // Format times to HH:MM:SS and find/create interval
        const intervalId = await findOrCreateIntervalId(startTime, endTime);
        
        // Create menu schedule with interval ID (backend expects intervalid, not startTime/endTime)
        await callApi({
          method: "POST",
          url: `/management/hotel/restaurants/${restaurantId}/menus/${selectedMenuId}/schedules`,
          data: {
            dayofweek: day,
            intervalid: intervalId,
            scheduletype: "Availability",
            isactive: true,
            notes: null,
          },
        });

        // Refresh menu schedules
        await fetchMenuSchedules(restaurantId, selectedMenuId);

        await queryClient.invalidateQueries({
          queryKey: ["menu-items", hotelId, restaurantId, selectedMenuId],
        });
        await queryClient.invalidateQueries({
          queryKey: ["menu-schedules", hotelId, restaurantId, selectedMenuId],
        });
      } catch (err) {
        console.error("Error creating menu schedule:", err);
        throw err;
      }
    },
    [callApi, restaurantId, selectedMenuId, hotelId, findOrCreateIntervalId, fetchMenuSchedules]
  );

  const updateMenuScheduleForDay = useCallback(
    async (day: string, startTime: string, endTime: string) => {
      if (!restaurantId || !selectedMenuId || !hotelId) return;

      try {
        // Format times to HH:MM:SS and find/create interval
        const intervalId = await findOrCreateIntervalId(startTime, endTime);
        
        // Find all schedules for this day (there may be multiple)
        const schedulesForDay = menuSchedules.filter(
          (schedule) => schedule.dayOfWeek === day && schedule.restaurantMenuId === selectedMenuId
        );
        
        // If multiple schedules exist, prefer the most recently updated one
        // For now, use the first active one, or the first one if none are active
        const existingSchedule = schedulesForDay.find(s => s.isActive) || schedulesForDay[0];

        if (existingSchedule) {
          // Update existing schedule with new interval ID
          await callApi({
            method: "PUT",
            url: `/management/hotel/restaurants/${restaurantId}/menus/${selectedMenuId}/schedules/${existingSchedule.menuOperatingScheduleId}`,
            data: {
              intervalid: intervalId,
              dayofweek: day,
              scheduletype: existingSchedule.scheduleType || "Availability",
              isactive: true,
              notes: existingSchedule.notes || null,
            },
          });
        } else {
          // Create new schedule if none exists
          await callApi({
            method: "POST",
            url: `/management/hotel/restaurants/${restaurantId}/menus/${selectedMenuId}/schedules`,
            data: {
              dayofweek: day,
              intervalid: intervalId,
              scheduletype: "Availability",
              isactive: true,
              notes: null,
            },
          });
        }

        // Refresh menu schedules
        await fetchMenuSchedules(restaurantId, selectedMenuId);

        await queryClient.invalidateQueries({
          queryKey: ["menu-items", hotelId, restaurantId, selectedMenuId],
        });
        await queryClient.invalidateQueries({
          queryKey: ["menu-schedules", hotelId, restaurantId, selectedMenuId],
        });
      } catch (err) {
        console.error("Error updating menu schedule:", err);
        throw err;
      }
    },
    [callApi, restaurantId, selectedMenuId, hotelId, menuSchedules, findOrCreateIntervalId, fetchMenuSchedules]
  );

  const deleteMenuScheduleForDay = useCallback(
    async (day: string) => {
      if (!restaurantId || !selectedMenuId) return;

      try {
        // Find the existing schedule for this day
        const existingSchedule = menuSchedules.find(
          (schedule) => schedule.dayOfWeek === day
        );

        if (existingSchedule) {
          await deleteMenuSchedule(
            callApi,
            restaurantId,
            selectedMenuId,
            existingSchedule.menuOperatingScheduleId
          );
        }

        // Refresh menu schedules
        await fetchMenuSchedules(restaurantId, selectedMenuId);

        await queryClient.invalidateQueries({
          queryKey: ["menu-items", hotelId, restaurantId, selectedMenuId],
        });
        await queryClient.invalidateQueries({
          queryKey: ["menu-schedules", hotelId, restaurantId, selectedMenuId],
        });
      } catch (err) {
        console.error("Error deleting menu schedule:", err);
        throw err;
      }
    },
    [callApi, restaurantId, selectedMenuId, menuSchedules, fetchMenuSchedules]
  );

  return {
    items,
    restaurants,
    menus,
    selectedMenuId,
    setSelectedMenuId,
    isLoading,
    error,
    selectedItem,
    setSelectedItem,
    createItem,
    updateItem,
    updateItemPrice,
    deleteItem,
    fetchItems,
    pagination,
    setPage,
    setLimit,
    // Menu schedule state and functions
    menuSchedules,
    isLoadingMenuSchedules,
    menuSchedulesError,
    fetchMenuSchedules,
    createMenuScheduleForDay,
    updateMenuScheduleForDay,
    deleteMenuScheduleForDay,
  };
};
